import { useState } from "react";
import { Leaf, BadgeCheck, Sparkles, MapPin, Truck, ShieldCheck, Sprout, BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import { HERBS, HERB_IMG_FALLBACK } from "@/data/catalog";
import { HerbDialog } from "@/components/HerbDialog";

const REASONS = [
  {
    icon: Leaf,
    title: "100% Herbal Formulas",
    text: "Pure Ayurvedic herbs with no harmful chemicals — gentle on the body, safe for long-term use.",
    big: true,
  },
  {
    icon: BadgeCheck,
    title: "Certified Quality",
    text: "ISO 9001:2015, WHO-GMP, ISO 22000:2018 and FSSAI certified manufacturing.",
  },
  {
    icon: Sparkles,
    title: "Power of 17 Super Herbs",
    text: "Potent classical herbs blended into one capsule for complete wellness.",
  },
  {
    icon: MapPin,
    title: "Trusted Brand from Jaipur",
    text: "Manufactured in Jaipur, Rajasthan — the heart of India's herbal tradition.",
  },
  {
    icon: Truck,
    title: "Ships All Over India",
    text: "From metro cities to small towns — wellness delivered to your doorstep.",
  },
  {
    icon: ShieldCheck,
    title: "Safe & Effective",
    text: "Formulated for adults of all ages, under certified quality systems.",
  },
];

export const WhyChooseUs = () => (
  <section data-testid="why-us-section" id="why-us" className="py-16 sm:py-24 bg-white overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="max-w-2xl mb-12"
      >
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C05C46] mb-3">
          Why Eleven Stars
        </p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1c2826] mb-4">
          Wellness for Life, the Ayurvedic Way
        </h2>
        <p className="text-base text-[#4B5563] leading-relaxed">
          A trusted wellness brand from Jaipur bringing certified, purely herbal Ayurvedic
          supplements to homes across India.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {REASONS.map((r, i) => (
          <motion.div
            key={r.title}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}
            data-testid={`why-card-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`group rounded-xl border border-[#E5E0D8] p-7 shadow-sm hover:shadow-lg transition-all duration-300 cursor-default ${
              r.big
                ? "bg-[#1A4331] text-[#F9F6F0] hover:bg-[#0f2e21]"
                : "bg-[#F9F6F0] hover:bg-[#1A4331] hover:text-[#F9F6F0] active:bg-[#1A4331] active:text-[#F9F6F0]"
            }`}
          >
            <div
              className={`w-11 h-11 rounded-full flex items-center justify-center mb-4 animate-float-gentle transition-colors duration-300 ${
                r.big ? "bg-[#D4AF37]/20" : "bg-[#7C9A82]/20 group-hover:bg-[#D4AF37]/20"
              }`}
              style={{ animationDelay: `${i * 0.4}s` }}
            >
              <r.icon className={`w-5 h-5 transition-colors duration-300 ${r.big ? "text-[#D4AF37]" : "text-[#1A4331] group-hover:text-[#D4AF37]"}`} />
            </div>
            <h3 className={`font-display text-xl font-semibold mb-2 transition-colors duration-300 ${r.big ? "text-[#F9F6F0]" : "text-[#1c2826] group-hover:text-[#F9F6F0]"}`}>
              {r.title}
            </h3>
            <p className={`text-sm leading-relaxed transition-colors duration-300 ${r.big ? "text-[#F9F6F0]/75" : "text-[#4B5563] group-hover:text-[#F9F6F0]/80"}`}>
              {r.text}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export const HerbsSection = () => {
  const [activeHerb, setActiveHerb] = useState(null);

  return (
  <section data-testid="herbs-section" id="herbs" className="py-16 sm:py-24 bg-[#1A4331] text-[#F9F6F0] overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="grid lg:grid-cols-3 gap-10 mb-12 items-end"
      >
        <div className="lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D4AF37] mb-3">
            Inside Every Capsule
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
            The Power of 17 Super Herbs
          </h2>
          <p className="text-base text-[#F9F6F0]/75 leading-relaxed max-w-xl">
            Classical Ayurvedic botanicals — Ashwagandha, Shilajit, Safed Musli, Gokshura and more —
            blended the traditional way for modern wellness.
          </p>
          <p className="flex items-center gap-2 text-xs text-[#D4AF37] mt-3 font-medium">
            <BookOpen className="w-3.5 h-3.5" /> Tap any herb to read its full Ayurvedic story
          </p>
        </div>
        <div className="hidden lg:flex items-center gap-2 text-[#D4AF37] justify-end">
          <Sprout className="w-5 h-5 animate-float-gentle" />
          <span className="text-sm font-medium">Sourced from trusted herbal farms</span>
        </div>
      </motion.div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
        {HERBS.map((h, i) => (
          <motion.button
            key={h.name}
            type="button"
            onClick={() => setActiveHerb(h)}
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
            data-testid={`herb-card-${i}`}
            className="group text-left rounded-xl overflow-hidden border border-white/10 bg-white/5 hover:border-[#D4AF37]/50 hover:bg-white/10 transition-colors duration-300 cursor-pointer"
          >
            <div className="h-32 sm:h-36 overflow-hidden relative">
              <img
                src={h.image}
                alt={h.name}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = HERB_IMG_FALLBACK;
                }}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <span className="absolute bottom-2 right-2 flex items-center gap-1 text-[10px] font-semibold bg-black/45 backdrop-blur-sm text-white rounded-full px-2.5 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <BookOpen className="w-3 h-3" /> Read story
              </span>
            </div>
            <div className="p-4">
              <h3 className="font-display text-base sm:text-lg font-semibold leading-snug">{h.name}</h3>
              <p className="text-xs text-[#F9F6F0]/65 mt-1 leading-relaxed">{h.benefit}</p>
            </div>
          </motion.button>
        ))}
      </div>
    </div>

    <HerbDialog herb={activeHerb} onClose={() => setActiveHerb(null)} />
  </section>
  );
};
